import { useState, useRef, useEffect, memo } from "react";
import { GrMicrophone } from "react-icons/gr";
import { BsStopCircle } from "react-icons/bs";
import RecordRTC from "recordrtc";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import Countdown from "react-countdown";
import { useConfig } from "../../../contexts/ConfigContext";

const mimeType = "audio/webm";

const countdownRenderer = ({ minutes, seconds, completed }) => {
  if (completed) {
    return <span>Sending to server...</span>;
  } else {
    return (
      <span>
        <Typography
          variant="body2"
          color="text.secondary"
          style={{ fontFamily: "inherit" }}
        >
          {`0${minutes}`}:{seconds < 10 ? `0${seconds}` : seconds}
        </Typography>
      </span>
    );
  }
};

function AudioRecorder({ onMessageChange }) {
  const { config } = useConfig();
  const mediaRecorder = useRef(null);
  const timeoutID = useRef(null);

  const [permission, setPermission] = useState(false);
  const [stream, setStream] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [loadingTranscription, setLoadingTranscription] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (stream) {
      startRecording();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const getMicrophonePermission = async () => {
    getUserMedia({ audio: true, video: false })
      .then((streamData) => {
        setPermission(true);
        setStream(streamData);
      })
      .catch((err) => {
        console.log(err.name, err, err.code, err.message);
        if (err instanceof DOMException && err.name === "NotAllowedError") {
          setError(
            `${err.message}. Please enable microphone permission in your settings.`
          );
        } else if (
          err instanceof DOMException &&
          err.name === "NotFoundError"
        ) {
          setError("No microphone found.");
        } else {
          setError("No microphone permission.");
        }
      });
  };

  const getUserMedia = async (constraints) => {
    if (window.navigator.mediaDevices) {
      return window.navigator.mediaDevices.getUserMedia(constraints);
    }
    let legacyApi =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;
    if (legacyApi) {
      return new Promise(function (resolve, reject) {
        legacyApi.bind(window.navigator)(constraints, resolve, reject);
      });
    } else {
      setError("Your browser doesn't support microphone input");
    }
  };

  const startRecording = async () => {
    try {
      setRecordingStatus("recording");
      const browserIsSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );

      const options = {
        type: "audio",
        mimeType: mimeType,
      };
      if (browserIsSafari) {
        options.mimeType = "audio/mp4";
        options.recorderType = RecordRTC.StereoAudioRecorder;
      }
      const media = RecordRTC(stream, options);
      mediaRecorder.current = media;
      mediaRecorder.current.startRecording();

      timeoutID.current = setTimeout(() => {
        if (mediaRecorder.current.state === "recording") {
          stopRecording();
        }
      }, 30000);
    } catch (error) {
      setError("Error while starting the recording.");
    }
  };

  const stopRecording = () => {
    setRecordingStatus("inactive");
    if (timeoutID.current) {
      clearTimeout(timeoutID.current);
      timeoutID.current = null;
    }
    mediaRecorder.current.stopRecording(() => {
      const audioBlob = mediaRecorder.current.getBlob();
      sendAudioToServer(audioBlob);
    });
  };

  const sendAudioToServer = async (blob) => {
    setLoadingTranscription(true);
    const formData = new FormData();
    formData.append("audio", blob, "recording.wav");
    formData.append(
      "language",
      config.metadataOptions?.language?.value ??
        config.metadataOptions?.language?.defaultOption
    );
    const queryParams = new URLSearchParams({
      tenant_id: config?.appId,
    });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_VOICE_API_URL}?${queryParams}`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await response.json();
      onMessageChange(data.trim());
    } catch (error) {
      // make message shorter
      setError("Error while trying to transcribe.");
    }
    setLoadingTranscription(false);
  };

  return (
    <div>
      {error && (
        <Alert
          fullWidth
          onClose={() => setError(null)}
          sx={{
            position: "absolute",
            bottom: 60,
            left: 0,
            border: "1px solid #ffa6a6",
          }}
          severity="error"
        >
          {error}
        </Alert>
      )}
      <main>
        <div className="audio-controls">
          {!permission ? (
            <Button
              onClick={getMicrophonePermission}
              sx={{
                minWidth: 40,
                minHeight: 38,
                ml: 1,
                borderRadius: 30,
                backgroundColor: config.color.primary,
                "@media (hover: hover)": {
                  "&:hover": {
                    backgroundColor: config.color.primary,
                    opacity: 0.9,
                  },
                },
              }}
              variant="contained"
              disableRipple
            >
              <GrMicrophone fontSize={18} />
            </Button>
          ) : null}
          {permission && recordingStatus === "inactive" ? (
            <Button
              onClick={startRecording}
              disabled={loadingTranscription}
              sx={{
                minWidth: 40,
                minHeight: 38,
                ml: 1,
                borderRadius: 30,
                backgroundColor: config.color.primary,
                "@media (hover: hover)": {
                  "&:hover": {
                    backgroundColor: config.color.primary,
                    opacity: 0.9,
                  },
                },
              }}
              variant="contained"
              disableRipple
            >
              {loadingTranscription ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <GrMicrophone fontSize={18} />
              )}
            </Button>
          ) : null}
          {recordingStatus === "recording" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Countdown
                style={{ fontFamily: "inherit" }}
                date={Date.now() + 30000}
                renderer={countdownRenderer}
              />
              <Button
                onClick={stopRecording}
                color="error"
                sx={{
                  minWidth: 40,
                  minHeight: 38,
                  ml: 1,
                  borderRadius: 30,
                }}
                variant="contained"
                disableRipple
              >
                <BsStopCircle fontSize={20} />
              </Button>
            </Box>
          ) : null}
        </div>
      </main>
    </div>
  );
}
export default memo(AudioRecorder);
